import React, { FunctionComponent } from "react";
import styles from "./TextVideo.module.scss";
import TextVideoContent from "./TextVideoContent.model";

import { useInView } from "react-intersection-observer";
import Anchor from "components/Anchor/Anchor";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import formatDynamicTextColor from "blocks/helpers/formatDynamicTextColor";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import getVideoHeightRatio from "app/media/helpers/getVideoHeightRatio";
import getWrapperStyles from "blocks/helpers/getWrapperStyles";
import isBrowserContext from "helpers/isBrowserContext";
import VideoViewer from "components/VideoViewer/VideoViewer";
import VideoEmbed from "components/VideoEmbed/VideoEmbed";
import BlockButton from "components/BlockButton/BlockButton";

const TextVideo: FunctionComponent<TextVideoContent> = (props) => {
    const processedHeadline = getProcessedRichtext(formatDynamicTextColor(props.content.headline));
    const processedBodyText = getProcessedRichtext(props.content.bodyText);
    const wrapperStyles = getWrapperStyles(props.settings);

    const videoHeightRatio = getVideoHeightRatio(props.content.mediaCrop, "Widescreen (16:9)");
    const mediaPositionClass = props.content.mediaPosition === "Media Right" ? ` ${styles.has_media_right}` : undefined;
    const isAutoplay = props.content.mediaAutoplay ? props.content.mediaAutoplay : false;
    const isLoop = props.content.mediaLoop ? props.content.mediaLoop : false;
    const isSoundButton = props.content.soundButton ? props.content.soundButton : false;

    const { ref, inView } = useInView({ triggerOnce: true });
    const isBrowserClass = isBrowserContext() ? styles.isBrowser : undefined;
    const inViewClass = inView ? styles.isInView : undefined;

    const randomNumber = Math.floor(Math.random() * 1000000);

    const buttonBgColor = props.settings?.buttonBackgroundColor?.color || "#d8e8c5";
    const buttonTextColor = props.settings?.buttonTextColor?.color || "#000000";
    const buttonHoverBackgroundColor = props.settings?.buttonHoverBackgroundColor?.color || "";
    const buttonHoverTextColor = props.settings?.buttonHoverColor?.color || "";

    return (
        <div
            className={combineClasses([styles.wrapper, ...wrapperStyles])}
            style={
                {
                    "--btn-hover-bg": `${buttonHoverBackgroundColor}`,
                    "--btn-hover-color": `${buttonHoverTextColor}`,
                } as React.CSSProperties
            }
        >
            <div className={combineClasses([styles.inner, mediaPositionClass])}>
                <div className={styles.media}>
                    {/* Media */}
                    {props.content.media ? (
                        <VideoViewer
                            soundButton={isSoundButton}
                            key={typeof props.content.media === "string" ? props.content.media : props.content.media.id}
                            className={styles.video}
                            src={typeof props.content.media === "string" ? props.content.media : props.content.media?.url}
                            heightRatio={videoHeightRatio}
                            alt={props.content.mediaAlternate}
                            autoplay={isAutoplay}
                            loop={isLoop}
                        />
                    ) : null}

                    {/* Video Embed */}
                    {props.content.videoUrl ? (
                        <VideoEmbed
                            soundButton={isSoundButton}
                            key={randomNumber}
                            className={styles.video}
                            src={props.content.videoUrl}
                            heightRatio={0.5625}
                            alt={props.content.mediaAlternate}
                            autoplay={isAutoplay}
                            loop={isLoop}
                        />
                    ) : null}

                    {/* Caption */}
                    {props.content.mediaCaption && props.content.mediaCaption.length > 0 ? (
                        <div className={styles.caption}>{props.content.mediaCaption}</div>
                    ) : null}
                </div>

                <div
                    className={combineClasses([styles.content, isBrowserClass, inViewClass])}
                    ref={ref}
                >
                    {/* Tagline */}
                    {props.content.tagline ? <span className={styles.tagline}>{props.content.tagline}</span> : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading
                            className={styles.headline}
                            headlineSize={props.content.headlineSize}
                            headlineWeight={props.content.headlineWeight}
                            innerHtml={processedHeadline}
                        ></DynamicHeading>
                    ) : null}

                    {/* Introductory */}
                    {props.content.introductory ? <p className={styles.introductory}>{props.content.introductory}</p> : null}

                    {/* Body Text */}
                    {processedBodyText ? (
                        <div
                            className={styles.bodyText}
                            dangerouslySetInnerHTML={processedBodyText}
                        ></div>
                    ) : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div className={styles.buttons}>
                            {props.content.buttons.map((button) => {
                                return (
                                    <BlockButton
                                        key={button.contentUdi}
                                        label={button?.label}
                                        link={button?.link}
                                        target={button?.target}
                                        borderColor={button?.borderColor}
                                        backgroundColor={button?.backgroundColor}
                                        textColor={button?.textColor}
                                        backgroundHoverColor={button?.backgroundHoverColor}
                                        hoverTextColor={button?.hoverTextColor}
                                    />
                                );
                            })}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default TextVideo;
